import { isFiniteNumber } from "@bitwarden/web-vault/app/shared/utils/helper-number-format";
import { DeviceDetectorService } from "ngx-device-detector";
import {
  AfterViewInit,
  ApplicationRef,
  computed,
  Component,
  ElementRef,
  HostListener,
  inject,
  model,
  ModelSignal,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { PlatformUtilsService } from "@vault/common/abstractions/platformUtils.service";
import { ActivatedRoute, Router } from "@angular/router";
import { AppStateStoreService } from "../../services/store/app-state/app-state.store.service";
import {
  CreditsService,
  FinalizedStripeReturn,
} from "../../services/syncing/gloss-credits/credits.service";
import { AdStateService } from "../../services/credits/ad-state.service";
import { RoleAccessService } from "../../services/permission/role-access.service";
import {
  PointsPurchaseComponent,
  PurchaseTab,
} from "./components/points-purchase/points-purchase.component";
import { ProgressStatus } from "@vault/web-vault/app/models/types/account.types";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GuideTitleEnum } from "@vault/web-vault/app/models/enum/guide.enum";
import { ConfirmationDialogService } from "@vault/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@vault/web-vault/app/models/enum/notification.enum";
import { DialogWrapperComponent } from "@vault/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { RIGHT_PANEL_CONFIG } from "@vault/web-vault/app/models/constants/global.constants";
import { PURCHASE_ID_KEY, PURCHASE_ID_PACK_KEY } from "../../models/constants/credits.constants";
import { ReferralApi } from "../../services/DataService/referral/referral.api";
import { GlossNotificationHandlerService } from "@vault/web-vault/app/services/notifications/gloss-notification-handler.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import {
  buildCashReceipt,
  openCreditsAddedDialog,
} from "./components/credits-added/credits-added.component";
import { openPurchaseProcessingDialog } from "./components/purchase-processing/purchase-processing.component";
import { PlatinumCardComponent } from "./components/platinum-card/platinum-card.component";
import { TotalCreditsDisplayComponent } from "./components/total-credits-display/total-credits-display.component";

// Must match the tw-duration-300 flip transition on the mobile card-swap wrapper in
// credits.component.html — the glare should only replay once the swap has settled.
const CARD_SWAP_TRANSITION_MS = 300;
const RAIL_THUMB_HIDE_DELAY_MS = 1000;
// Pointer travel before a press on the rail counts as a drag rather than a click on a card.
const RAIL_DRAG_THRESHOLD_PX = 5;
const RAIL_AUTOSCROLL_EDGE_PX = 48;
// Time-based so the speed doesn't depend on the display's refresh rate (~14px per frame at 60Hz).
const RAIL_AUTOSCROLL_PX_PER_MS = 0.84;
// A long frame (tab in the background, a busy main thread) must not make the rail jump.
const RAIL_AUTOSCROLL_MAX_FRAME_MS = 50;
// Fraction of the distance to the neighbouring card's slot the dragged card must travel before
// the two swap. Swapping straight back needs a longer drag (the return threshold) so a card
// held right at the boundary doesn't flip on every pointer move.
const RAIL_SWAP_THRESHOLD = 0.3;
const RAIL_SWAP_BACK_THRESHOLD = 1 - RAIL_SWAP_THRESHOLD / 2;
const RAIL_SETTLE_MS = 150;
// Above the guide's z-[100] cards, so a held card stays on top in the guide too.
const RAIL_DRAG_Z_INDEX = "101";

type RailCardKey = "platinum" | "gold";
const RAIL_CARD_LABEL_KEYS: Record<RailCardKey, string> = {
  platinum: "platinumCredits",
  gold: "goldCredits",
};

// Horizontal travel (px) before a drag on the mobile card stack counts as a swipe-to-swap.
const SWIPE_COMMIT_DISTANCE_PX = 40;
// Movement before we decide whether the gesture is a horizontal swipe or a vertical scroll.
const SWIPE_LOCK_DISTANCE_PX = 8;
// The front card only nudges toward the finger (damped, capped) so it never drifts far from the
// card behind it; the swap itself is decided on the raw finger travel above. The cap matches the
// stack wrapper's px-4 so the card is never clipped at the column edge.
const SWIPE_FOLLOW_RATIO = 0.4;
const SWIPE_MAX_OFFSET_PX = 16;

@Component({
  selector: "app-credits",
  templateUrl: "./credits.component.html",
})
export class CreditsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("viewPointsElement", { static: false }) targetRef!: ElementRef<HTMLElement>;
  @ViewChild("mobilePlatinumCard") private mobilePlatinumCard?: PlatinumCardComponent;
  @ViewChild("mobileTotalCreditsCard")
  private mobileTotalCreditsCard?: TotalCreditsDisplayComponent;
  @ViewChild("balanceCardRail") private balanceCardRail?: ElementRef<HTMLElement>;
  private unsubscribe$ = new Subject<void>();
  protected creditsService = inject(CreditsService);
  adStateService = inject(AdStateService);
  private notifyService = inject(ConfirmationDialogService);
  private dialog: MatDialog = inject(MatDialog);
  private router: Router = inject(Router);
  protected appState: AppStateStoreService = inject(AppStateStoreService);
  deviceService: DeviceDetectorService = inject(DeviceDetectorService);
  protected platformUtils: PlatformUtilsService = inject(PlatformUtilsService);
  protected route: ActivatedRoute = inject(ActivatedRoute);
  private referralApi = inject(ReferralApi);
  private glossNotificationHandler = inject(GlossNotificationHandlerService);
  private logService = inject(LogService);
  private i18nService = inject(I18nService);
  private stateService = inject(StateService);
  private roleAccessService = inject(RoleAccessService);
  private appRef = inject(ApplicationRef);
  private ngZone = inject(NgZone);

  // Cash Back (Amazon Gift Card) is beta-only; everyone else sees Vault Plans with no selector.
  protected readonly isBetaUser = this.roleAccessService.isBetaUserSignal;

  // Guards the Stripe-return handling below from re-running when queryParams re-emits
  // after router.navigate({queryParams: {}, replaceUrl: true}) clears stripe_session.
  private stripeReturnHandled = false;

  protected tryAgainLaterInTutorial: ModelSignal<boolean> = model(false);
  protected isWatchAdTaskSucceeded: ModelSignal<boolean> = model(false);
  protected isFromGuide: WritableSignal<boolean> = signal(false);
  protected isFromGuideWatchAds: WritableSignal<boolean> = signal(false);
  protected isFromGuideViewPoints: WritableSignal<boolean> = signal(false);
  protected guideTitle: WritableSignal<string> = signal("");
  protected readonly creditsGuideBottomAction = computed<"back" | "none">(() =>
    (this.isFromGuideWatchAds() &&
      (this.tryAgainLaterInTutorial() || this.isWatchAdTaskSucceeded())) ||
    this.isFromGuideViewPoints()
      ? "back"
      : "none",
  );
  protected get isMobileApp(): boolean {
    return this.platformUtils.isMobileDevice();
  }

  protected get isPhone(): boolean {
    return this.isMobileApp || this.deviceService.isMobile();
  }

  protected railFadeState: WritableSignal<"none" | "start" | "end" | "both"> = signal("none");
  protected railThumb: WritableSignal<{ left: number; width: number }> = signal({
    left: 0,
    width: 100,
  });
  protected railThumbVisible: WritableSignal<boolean> = signal(false);
  private railThumbHideTimer: ReturnType<typeof setTimeout> | null = null;
  private railResizeFrame: number | null = null;
  protected railDragging: WritableSignal<boolean> = signal(false);
  private railDragStart: {
    pointerId: number;
    x: number;
    scrollLeft: number;
    // Set when the press landed on a card: the drag then moves/reorders that card instead of
    // scrolling the rail.
    card: HTMLElement | null;
    cardKey: RailCardKey | null;
    grabOffset: number;
    pointerX: number;
  } | null = null;
  private railCardTranslate = 0;
  private railReleaseTimers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();
  private railLastSwapDirection: 1 | -1 | null = null;
  private railAutoScrollFrame: number | null = null;
  protected railCardOrder: WritableSignal<RailCardKey[]> = signal(["platinum", "gold"]);
  protected railReorderAnnouncement: WritableSignal<string> = signal("");
  // Drops the document-level move/up listeners that only exist while a press is in progress.
  private removeRailPointerListeners: (() => void) | null = null;

  isExpanded = false;
  isEarnCreditsExpanded = false;
  isLoading = false;
  isPlatinumCardFront = false;
  protected swipeOffset: WritableSignal<number> = signal(0);
  protected swipeDragging: WritableSignal<boolean> = signal(false);
  private swipeStart: { x: number; y: number; pointerId: number } | null = null;
  private swipeSuppressTap = false;
  private cardSwapGlareTimer: ReturnType<typeof setTimeout> | null = null;
  isCopied = false;
  discountCode = "";
  referralCount = 0;

  // Vault Plans / Cash Back (Amazon Gift Card) card
  isRedemptionExpanded = false;
  isVaultPlans = true;
  isCashBack = false;
  isCouponsExpanded = false;
  isRecentActivityExpanded = false;
  isMyDiscountCodeExpanded = false;

  async ngOnInit() {
    if (!this.deviceService.isMobile()) {
      this.isEarnCreditsExpanded = true;
      this.isRedemptionExpanded = true;
      this.isCouponsExpanded = true;
      this.isRecentActivityExpanded = true;
      this.isMyDiscountCodeExpanded = true;
    }
    this.adStateService.loadStateFromStorage();
    this.adStateService.checkAndResetDaily();
    this.glossNotificationHandler
      .drainPendingReferralCredits()
      .catch((err) => this.logService.error(`Failed to drain pending referral credits: ${err}`));

    // Refresh the referral code + "N friends referred" counter whenever a
    // referrer credit is applied (e.g. a referee uses the code while this page
    // is open, or the credit arrives via WS push).
    this.glossNotificationHandler.referralCreditApplied$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.loadReferralCode());

    // Initial load, independent of drain outcome. The subscription above
    // handles subsequent refreshes when a drain applies credits.
    this.loadReferralCode();

    this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
      // Only a recognised guide reached with isFromGuide=true puts the page in guide mode, so the
      // mask (isFromGuide) can never show without a tooltip that gives the way out, and a stale
      // `guide` param on its own does nothing.
      const guide = params["isFromGuide"] === "true" ? params["guide"] : undefined;
      this.guideTitle.set(params["guide"]);
      this.isFromGuideWatchAds.set(guide === GuideTitleEnum.watchAdsToEarn);
      this.isFromGuideViewPoints.set(guide === GuideTitleEnum.viewYourCredits);
      this.isFromGuide.set(this.isFromGuideWatchAds() || this.isFromGuideViewPoints());

      if (this.isFromGuideWatchAds()) {
        this.isEarnCreditsExpanded = true;
      }

      if (this.stripeReturnHandled) {
        return;
      }
      const stripeSession = params["stripe_session"];
      // The return URL's purchase_id counts too: a checkout started on another device never stored
      // one here. finalizePendingStripeReturn captures it.
      const pendingPurchase =
        this.creditsService.returnUrlPurchaseId() || localStorage.getItem(PURCHASE_ID_KEY);
      if (stripeSession || pendingPurchase) {
        this.stripeReturnHandled = true;
        if (stripeSession) {
          this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
        }
        if (pendingPurchase) {
          void this.handleStripeReturn();
        } else {
          this.onPurchaseCreditsClick();
          this.notifyService.notify(NotificationEnum.error, "errorOccurred", "purchaseFail");
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.updateRailFadeState();
  }

  @HostListener("window:resize")
  protected onWindowResize(): void {
    if (this.railResizeFrame !== null) {
      return;
    }
    this.railResizeFrame = requestAnimationFrame(() => {
      this.railResizeFrame = null;
      this.updateRailFadeState();
    });
  }

  updateRailFadeState(): void {
    const el = this.balanceCardRail?.nativeElement;
    // scrollWidth is 0 while the rail is display:none (stack layout), which would make the
    // thumb maths NaN.
    if (!el || el.scrollWidth === 0) {
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    this.railThumb.set({
      left: (el.scrollLeft / el.scrollWidth) * 100,
      width: (el.clientWidth / el.scrollWidth) * 100,
    });
    if (maxScroll <= 1) {
      this.railFadeState.set("none");
    } else if (el.scrollLeft <= 1) {
      this.railFadeState.set("end");
    } else if (el.scrollLeft >= maxScroll - 1) {
      this.railFadeState.set("start");
    } else {
      this.railFadeState.set("both");
    }
  }

  // The scrollbar is hidden, so map a vertical mouse wheel onto the rail. Only claim the event
  // while the rail can still move that way; otherwise let the page keep scrolling.
  protected onRailWheel(event: WheelEvent): void {
    const el = this.balanceCardRail?.nativeElement;
    if (!el || event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
      return;
    }
    const delta = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 16 : event.deltaY;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const canScroll = delta > 0 ? el.scrollLeft < maxScroll - 1 : el.scrollLeft > 1;
    if (canScroll) {
      event.preventDefault();
      el.scrollLeft += delta;
    }
  }

  // Mouse drag, alongside native scroll (touch and pen already pan natively). Pressing on a card
  // drags that card along with the pointer and swaps it with its neighbour once it passes the
  // neighbour's centre; pressing anywhere else on the rail drags it to scroll.
  protected onRailPointerDown(event: PointerEvent): void {
    const el = this.balanceCardRail?.nativeElement;
    if (!el || event.pointerType !== "mouse" || event.button !== 0) {
      return;
    }
    // The drag maths assumes left-to-right; right-to-left keeps native scrolling and the keyboard.
    if (getComputedStyle(el).direction === "rtl") {
      return;
    }
    const card = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-rail-card]");
    if (!card && this.railFadeState() === "none") {
      return;
    }
    this.railCardTranslate = 0;
    this.railLastSwapDirection = null;
    this.railDragStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      scrollLeft: el.scrollLeft,
      card: card ?? null,
      cardKey: (card?.dataset["railCard"] as RailCardKey | undefined) ?? null,
      grabOffset: card ? event.clientX - card.getBoundingClientRect().left : 0,
      pointerX: event.clientX,
    };
    this.addRailPointerListeners();
  }

  // Move/up are only needed while a button is held, so they are attached on press and run outside
  // the zone: a template (pointermove) binding would trigger a change detection pass for every
  // mouse move over the rail, dragging or not. They sit on the document so a press that ends
  // outside the rail, before the pointer is captured, still ends here.
  private addRailPointerListeners(): void {
    this.removeRailPointerListeners?.();
    const move = (event: PointerEvent) => this.onRailPointerMove(event);
    const end = (event: PointerEvent) => this.onRailPointerEnd(event);
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", end);
      document.addEventListener("pointercancel", end);
    });
    this.removeRailPointerListeners = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", end);
      document.removeEventListener("pointercancel", end);
      this.removeRailPointerListeners = null;
    };
  }

  private onRailPointerMove(event: PointerEvent): void {
    const el = this.balanceCardRail?.nativeElement;
    const start = this.railDragStart;
    if (!el || !start || event.pointerId !== start.pointerId) {
      return;
    }
    // The button was released where we couldn't see it (outside the rail before the pointer was
    // captured), so this press is over: don't start a drag from a hover.
    if ((event.buttons & 1) === 0) {
      this.onRailPointerEnd(event);
      return;
    }
    start.pointerX = event.clientX;
    const dx = event.clientX - start.x;
    if (!this.railDragging()) {
      if (Math.abs(dx) < RAIL_DRAG_THRESHOLD_PX) {
        return;
      }
      // Outside the zone here, so enter it for the class bindings that follow railDragging.
      this.ngZone.run(() => this.railDragging.set(true));
      el.setPointerCapture(event.pointerId);
      if (start.card) {
        start.card.style.transition = "none";
        start.card.style.zIndex = RAIL_DRAG_Z_INDEX;
        this.startRailAutoScroll();
      }
    }
    if (start.card) {
      this.updateDraggedCard();
    } else {
      el.scrollLeft = start.scrollLeft - dx;
    }
  }

  private onRailPointerEnd(event: PointerEvent): void {
    const start = this.railDragStart;
    if (!start || event.pointerId !== start.pointerId) {
      return;
    }
    this.removeRailPointerListeners?.();
    // Listeners run outside the zone: the state below drives bindings, so settle it inside.
    this.ngZone.run(() => {
      const el = this.balanceCardRail?.nativeElement;
      if (this.railDragging()) {
        if (el?.hasPointerCapture(event.pointerId)) {
          el.releasePointerCapture(event.pointerId);
        }
        this.railDragging.set(false);
      }
      this.stopRailAutoScroll();
      this.railDragStart = null;
      this.railCardTranslate = 0;
      if (start.card) {
        this.settleRailCard(start.card);
      }
      this.updateRailFadeState();
    });
  }

  // Keyboard alternative to dragging: Alt+Left/Right moves the focused card past its neighbour.
  protected onRailKeydown(event: KeyboardEvent): void {
    const rail = this.balanceCardRail?.nativeElement;
    if (
      !rail ||
      !event.altKey ||
      this.railDragging() ||
      (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
    ) {
      return;
    }
    const card = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-rail-card]");
    const key = card?.dataset["railCard"] as RailCardKey | undefined;
    if (!card || !key) {
      return;
    }
    const order = this.railCardOrder();
    const index = order.indexOf(key);
    // Arrow keys follow the screen, so in a right-to-left rail ArrowRight moves a card earlier.
    const rtl = getComputedStyle(rail).direction === "rtl";
    const step = (event.key === "ArrowRight") !== rtl ? 1 : -1;
    const target = index + step;
    const label = this.i18nService.t(RAIL_CARD_LABEL_KEYS[key]);
    if (target < 0 || target >= order.length) {
      this.railReorderAnnouncement.set(this.i18nService.t("rewardsCardCannotMove", label));
      return;
    }
    const other = rail.querySelector<HTMLElement>(`[data-rail-card="${order[target]}"]`);
    if (!other) {
      return;
    }
    // Only claim the key when a card actually moves, so Alt+Left still goes Back everywhere else.
    event.preventDefault();
    const cardBefore = card.getBoundingClientRect().left;
    const otherBefore = other.getBoundingClientRect().left;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    this.railCardOrder.set(next);
    this.railReorderAnnouncement.set(
      this.i18nService.t(
        "rewardsCardMoved",
        label,
        (target + 1).toString(),
        order.length.toString(),
      ),
    );
    this.appRef.tick();
    // Moving a node in the DOM can drop focus, so put it back and keep it in view.
    card.focus({ preventScroll: true });
    card.scrollIntoView({ block: "nearest", inline: "nearest" });
    this.slideRailCard(card, cardBefore);
    this.slideRailCard(other, otherBefore);
  }

  // Keeps the dragged card under the pointer and swaps it with its neighbour when it crosses
  // that neighbour's centre. Positions are measured in screen space so rail scrolling mid-drag
  // doesn't move the card away from the pointer.
  private updateDraggedCard(): void {
    const start = this.railDragStart;
    const card = start?.card;
    const rail = this.balanceCardRail?.nativeElement;
    if (!start || !card || !start.cardKey || !rail) {
      return;
    }
    const layoutLeft = () => card.getBoundingClientRect().left - this.railCardTranslate;
    // The card can't leave the row of cards: a transform widens the rail's scrollable area, so
    // holding a card past the end would otherwise keep the auto-scroll running.
    const bounds = this.railCardBounds(rail, card);
    const desiredLeft = Math.min(
      Math.max(start.pointerX - start.grabOffset, bounds.min),
      Math.max(bounds.min, bounds.max),
    );
    const order = this.railCardOrder();
    const index = order.indexOf(start.cardKey);
    const travelled = desiredLeft - layoutLeft();
    const direction = travelled > 0 ? 1 : -1;
    const neighbourKey = order[index + direction];
    const neighbour = neighbourKey
      ? rail.querySelector<HTMLElement>(`[data-rail-card="${neighbourKey}"]`)
      : null;
    if (neighbour) {
      // Visual position, so a neighbour that is still sliding doesn't jump when it moves again.
      const neighbourBefore = neighbour.getBoundingClientRect().left;
      // Widths rather than rects, so a neighbour mid-slide can't skew the distance.
      const slotDistance =
        (direction > 0 ? card.offsetWidth : neighbour.offsetWidth) + this.railGap(rail);
      const threshold =
        this.railLastSwapDirection === -direction ? RAIL_SWAP_BACK_THRESHOLD : RAIL_SWAP_THRESHOLD;
      if (Math.abs(travelled) > slotDistance * threshold) {
        this.railLastSwapDirection = direction;
        const next = [...order];
        [next[index], next[index + direction]] = [next[index + direction], next[index]];
        this.railCardOrder.set(next);
        // Re-render now so the DOM (and tab) order follows the visual order and the translate
        // below is measured against the new layout. This can run outside the zone (auto-scroll),
        // so render now instead of waiting for a zone turn.
        this.appRef.tick();
        this.slideRailCard(neighbour, neighbourBefore);
      }
    }
    this.railCardTranslate = desiredLeft - layoutLeft();
    card.style.transform = `translateX(${this.railCardTranslate}px)`;
  }

  // Screen-space range the dragged card's left edge can take: the first slot to the last slot.
  // Derived from widths rather than card rects so a neighbour mid-slide can't skew it.
  private railCardBounds(rail: HTMLElement, card: HTMLElement): { min: number; max: number } {
    const style = getComputedStyle(rail);
    const gap = this.railGap(rail);
    const cards = Array.from(rail.querySelectorAll<HTMLElement>("[data-rail-card]"));
    const total = cards.reduce((sum, c) => sum + c.offsetWidth, 0) + gap * (cards.length - 1);
    const min =
      rail.getBoundingClientRect().left +
      rail.clientLeft +
      (parseFloat(style.paddingLeft) || 0) -
      rail.scrollLeft;
    return { min, max: min + total - card.offsetWidth };
  }

  private railGap(rail: HTMLElement): number {
    return parseFloat(getComputedStyle(rail).columnGap) || 0;
  }

  private railMotionMs(): number {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : RAIL_SETTLE_MS;
  }

  // Animates a card that was moved by a swap from where it was on screen (fromLeft) to its new
  // slot.
  private slideRailCard(card: HTMLElement, fromLeft: number): void {
    // Drop any slide still in flight so the new layout position can be measured.
    card.style.transition = "none";
    card.style.transform = "";
    const ms = this.railMotionMs();
    const delta = fromLeft - card.getBoundingClientRect().left;
    if (!ms || !delta) {
      card.style.transition = "";
      return;
    }
    card.style.transform = `translateX(${delta}px)`;
    // Commit the starting position before enabling the transition.
    void card.offsetWidth;
    card.style.transition = `transform ${ms}ms ease`;
    card.style.transform = "";
    this.releaseRailCardLater(card, ms);
  }

  // Lets go of the dragged card: it eases from the pointer into its slot.
  private settleRailCard(card: HTMLElement): void {
    const ms = this.railMotionMs();
    card.style.transition = ms ? `transform ${ms}ms ease` : "";
    card.style.transform = "";
    this.releaseRailCardLater(card, ms);
  }

  // Clears the inline motion styles once a card's transition is done, unless it has been picked
  // up again, and keeps only the latest timer per card so an earlier one can't cut a newer
  // animation short.
  private releaseRailCardLater(card: HTMLElement, ms: number): void {
    const pending = this.railReleaseTimers.get(card);
    if (pending !== undefined) {
      clearTimeout(pending);
    }
    this.railReleaseTimers.set(
      card,
      setTimeout(() => {
        this.railReleaseTimers.delete(card);
        if (this.railDragStart?.card !== card) {
          card.style.transition = "";
          card.style.zIndex = "";
        }
      }, ms),
    );
  }

  // While a card is held near either end of the rail, keep scrolling so it can reach the cards
  // that are cut off, even when the pointer is stationary.
  private startRailAutoScroll(): void {
    let lastFrame: number | null = null;
    const tick = (now: number) => {
      const el = this.balanceCardRail?.nativeElement;
      const start = this.railDragStart;
      if (!el || !start?.card) {
        this.railAutoScrollFrame = null;
        return;
      }
      const elapsed =
        lastFrame === null ? 0 : Math.min(now - lastFrame, RAIL_AUTOSCROLL_MAX_FRAME_MS);
      lastFrame = now;
      const distance = RAIL_AUTOSCROLL_PX_PER_MS * elapsed;
      const rect = el.getBoundingClientRect();
      let step = 0;
      if (start.pointerX > rect.right - RAIL_AUTOSCROLL_EDGE_PX) {
        step = distance;
      } else if (start.pointerX < rect.left + RAIL_AUTOSCROLL_EDGE_PX) {
        step = -distance;
      }
      if (step !== 0) {
        const before = el.scrollLeft;
        el.scrollLeft += step;
        if (el.scrollLeft !== before) {
          this.updateDraggedCard();
        }
      }
      this.railAutoScrollFrame = requestAnimationFrame(tick);
    };
    // The loop runs every frame for as long as a card is held; keep it out of the zone so it
    // doesn't trigger a change detection pass per frame.
    this.ngZone.runOutsideAngular(() => {
      this.railAutoScrollFrame = requestAnimationFrame(tick);
    });
  }

  private stopRailAutoScroll(): void {
    if (this.railAutoScrollFrame !== null) {
      cancelAnimationFrame(this.railAutoScrollFrame);
      this.railAutoScrollFrame = null;
    }
  }

  protected onRailScroll(): void {
    this.updateRailFadeState();
    this.railThumbVisible.set(true);
    if (this.railThumbHideTimer) {
      clearTimeout(this.railThumbHideTimer);
    }
    this.railThumbHideTimer = setTimeout(() => {
      this.railThumbVisible.set(false);
      this.railThumbHideTimer = null;
    }, RAIL_THUMB_HIDE_DELAY_MS);
  }

  private async handleStripeReturn(): Promise<void> {
    // Read before finalizing — finalizePendingStripeReturn clears this key in its `finally`. It may
    // be missing (bootstrap's own finalize cleared it first, or the checkout started on another
    // device) or belong to another purchase: it's used only if its purchaseId is the one finalized.
    const packRaw = localStorage.getItem(PURCHASE_ID_PACK_KEY);

    // finalizePurchase can take up to ~105s with a slow Stripe webhook; show that it's working
    // rather than nothing, so the user doesn't conclude the payment failed and pay again.
    const processingRef = openPurchaseProcessingDialog(this.dialog);

    let finalized: FinalizedStripeReturn | null;
    try {
      // Idempotent — bootstrap may already be handling this same return.
      finalized = await this.creditsService.finalizePendingStripeReturn();
    } catch (err) {
      // finalizePurchase never rejects (a failed poll resolves { status: "pending" } and toasts
      // "processing"); this catches the rest, e.g. its capturePurchaseIdFromReturnUrl hitting a
      // throwing localStorage write. Scoped to the finalize call alone so a receipt-building bug
      // below can't be misreported as a failed payment. Handled like "pending": the outcome is
      // unknown and Stripe may have charged, so no "failed" toast and no buy drawer inviting a
      // second payment.
      processingRef.close();
      this.logService.error(`Failed to finalize Stripe return: ${err}`);
      this.notifyService.notify(NotificationEnum.info, "processing", "creditsPurchaseProcessing");
      return;
    }
    processingRef.close();
    if (!finalized) {
      // Nothing to finalize — fall back to the plain purchase picker.
      this.onPurchaseCreditsClick();
      return;
    }
    const { purchaseId, detail } = finalized;

    if (detail?.status === "pending") {
      // Not confirmed either way: Stripe may already have charged, and the service has toasted
      // "payment received, being processed". Opening the buy drawer now would invite paying
      // twice, so leave it closed; login-recovery reconciles the purchase later.
      return;
    }
    if (detail?.status !== "completed") {
      // Failed — fall back to the plain purchase picker so the user can
      // try again, same as before this receipt screen existed.
      this.onPurchaseCreditsClick();
      return;
    }

    // The purchase completed and finalizePendingStripeReturn has already toasted success, so
    // nothing below may contradict that. Same guard as PointsPurchaseComponent's Apple path: if
    // building or opening the receipt throws, log it rather than leave an unhandled rejection
    // (the processing panel is already closed) or show a misleading "purchase failed".
    try {
      let pack: {
        purchaseId?: unknown;
        price?: unknown;
        currency?: unknown;
        credits?: unknown;
      } | null = null;
      try {
        pack = packRaw ? JSON.parse(packRaw) : null;
      } catch {
        // Corrupt pack metadata only costs the receipt's price line — the purchase completed.
      }
      if (pack?.purchaseId !== purchaseId) {
        // Another purchase's pack (or one stashed before packs were tagged): not this price.
        pack = null;
      }
      const email = await this.stateService.getEmail().catch(() => undefined);
      // null (not 0) when neither the backend nor the stashed pack says how much was added — the
      // receipt then drops the number rather than claiming "+0" next to a balance that went up.
      // The pack is untyped localStorage JSON, so each field is only used when it has the expected
      // type: credits and price when they're finite numbers, currency when it's a string.
      const goldAdded = [detail.credits, pack?.credits].find(isFiniteNumber) ?? null;
      const { subtitle, rows } = buildCashReceipt(this.i18nService, {
        goldAdded,
        newGoldBalance: isFiniteNumber(detail.newBalance)
          ? detail.newBalance
          : this.creditsService.totalGoldCredits(),
        paidAmount: isFiniteNumber(pack?.price) ? pack.price : undefined,
        paidCurrency: typeof pack?.currency === "string" ? pack.currency : undefined,
        transactionId: purchaseId,
        email,
      });
      openCreditsAddedDialog(this.dialog, goldAdded, subtitle, rows);
    } catch (err) {
      this.logService.error(
        `Stripe purchase ${purchaseId} completed but its receipt failed: ${err}`,
      );
    }
  }

  private loadReferralCode(): void {
    this.referralApi
      .getReferralCode()
      .then(({ referralCode, referralCount }) => {
        this.discountCode = referralCode;
        this.referralCount = referralCount;
      })
      .catch((err) => this.logService.error(`Failed to load referral code: ${err}`));
  }

  get progressArray(): ProgressStatus[] {
    const states = this.adStateService.adStates();
    const maxAds = this.adStateService.maxAdsPerDay;

    return Array(maxAds)
      .fill("empty")
      .map((_, i) => {
        const ad = states[i];
        if (!ad) {
          return "empty";
        }
        if (ad.watched) {
          return "complete";
        }
        if ((ad as any).isLoaded || (ad as any).hasNoAds) {
          return "interrupted";
        }
        return "empty";
      });
  }

  setVaultCashBackTab(tab: "vaultPlans" | "cashBack"): void {
    this.isVaultPlans = tab === "vaultPlans";
    this.isCashBack = tab === "cashBack";
  }

  // The "Get credits" button opens on the Platinum tab; the Stripe-return fallbacks above keep
  // the default "cash" since the user was mid cash purchase.
  onPurchaseCreditsClick(initialTab: PurchaseTab = "cash"): void {
    this.dialog.open(DialogWrapperComponent, {
      ...RIGHT_PANEL_CONFIG,
      data: {
        title: "getMoreGoldCredits",
        showBackButton: false,
        component: PointsPurchaseComponent,
        componentInputs: { initialTab },
      },
    });
  }

  copyDiscountCode(): void {
    navigator.clipboard
      .writeText(this.discountCode)
      .then(() => {
        this.isCopied = true;
        setTimeout(() => (this.isCopied = false), 2000);
      })
      .catch((err) => this.logService.error(`Failed to copy referral code: ${err}`));
  }

  // A swipe may also end in a click (browsers differ on whether pointer capture swallows it);
  // ignore that one so it doesn't swap twice.
  protected onCardTap(): void {
    if (this.swipeSuppressTap) {
      this.swipeSuppressTap = false;
      return;
    }
    this.togglePlatinumCard();
  }

  protected onSwipeStart(event: PointerEvent): void {
    // A new gesture begins: a click left over from the previous swipe can no longer arrive.
    this.swipeSuppressTap = false;
    // Touch/pen only (the stack is a phone interaction; mouse users click), and only the primary
    // pointer so a second finger can't replace the gesture in progress.
    if (event.pointerType === "mouse" || event.isPrimary === false) {
      return;
    }
    this.swipeStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
  }

  protected onSwipeMove(event: PointerEvent): void {
    const start = this.swipeStart;
    if (!start || start.pointerId !== event.pointerId) {
      return;
    }
    const dx = event.clientX - start.x;
    if (!this.swipeDragging()) {
      const dy = event.clientY - start.y;
      if (Math.abs(dx) < SWIPE_LOCK_DISTANCE_PX && Math.abs(dy) < SWIPE_LOCK_DISTANCE_PX) {
        return;
      }
      if (Math.abs(dy) > Math.abs(dx)) {
        // Vertical gesture: it's a page scroll, not ours.
        this.swipeStart = null;
        return;
      }
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      this.swipeDragging.set(true);
    }
    const follow = dx * SWIPE_FOLLOW_RATIO;
    this.swipeOffset.set(Math.max(-SWIPE_MAX_OFFSET_PX, Math.min(SWIPE_MAX_OFFSET_PX, follow)));
  }

  protected onSwipeEnd(event: PointerEvent): void {
    const start = this.swipeStart;
    if (!start || start.pointerId !== event.pointerId) {
      return;
    }
    const wasDragging = this.swipeDragging();
    const dx = event.clientX - start.x;
    this.resetSwipe();
    if (!wasDragging) {
      return;
    }
    // The click that follows a drag must not toggle again; onCardTap or the next pointerdown clears it.
    this.swipeSuppressTap = true;
    if (Math.abs(dx) >= SWIPE_COMMIT_DISTANCE_PX) {
      this.togglePlatinumCard();
    }
  }

  protected onSwipeCancel(event: PointerEvent): void {
    if (this.swipeStart?.pointerId === event.pointerId) {
      this.resetSwipe();
    }
  }

  private resetSwipe(): void {
    this.swipeStart = null;
    this.swipeDragging.set(false);
    this.swipeOffset.set(0);
  }

  togglePlatinumCard(): void {
    this.isPlatinumCardFront = !this.isPlatinumCardFront;

    if (this.cardSwapGlareTimer) {
      clearTimeout(this.cardSwapGlareTimer);
    }
    const isPlatinumFront = this.isPlatinumCardFront;
    this.cardSwapGlareTimer = setTimeout(() => {
      this.cardSwapGlareTimer = null;
      // Replay the glare on whichever card just swapped to the front, once the flip
      // transition has actually finished (rather than while it's still mid-flight).
      if (isPlatinumFront) {
        this.mobilePlatinumCard?.playGlare();
      } else {
        this.mobileTotalCreditsCard?.playGlare();
      }
    }, CARD_SWAP_TRANSITION_MS);
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  toggleRedemptionExpanded(): void {
    this.isRedemptionExpanded = !this.isRedemptionExpanded;
  }

  viewBundleHistory(): void {
    this.router.navigate(["/earn"]);
  }

  createDataBundle(): void {
    this.router.navigate(["/earn"]);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.adStateService.saveStateToStorage();
    this.adStateService.timerIntervals.forEach((interval) => clearInterval(interval));
    if (this.cardSwapGlareTimer) {
      clearTimeout(this.cardSwapGlareTimer);
    }
    if (this.railThumbHideTimer) {
      clearTimeout(this.railThumbHideTimer);
    }
    if (this.railResizeFrame !== null) {
      cancelAnimationFrame(this.railResizeFrame);
    }
    this.stopRailAutoScroll();
    this.removeRailPointerListeners?.();
    this.railReleaseTimers.forEach((timer) => clearTimeout(timer));
    this.railReleaseTimers.clear();
  }
}
